/**
 * Le journal — traduction des sorties du moteur en lignes lisibles.
 *
 * `resolve()` rend deux choses : un `log` (déjà écrit en français par le moteur : « Nuit 3 —
 * énergie remise à 100 », « Veille : 2 nouvelles idées en main ») et des `events` (des FAITS
 * structurés, sans texte — « le moteur n'écrit pas de texte de lore »).
 *
 * Règle de composition, pour ne rien dire deux fois : **le `log` est la trame**, et on n'ajoute
 * un `EngineEvent` que lorsqu'il porte un fait qu'aucune ligne de log ne raconte (paliers,
 * niveaux, désabonnements, rente, avertissements, fins). Les versements quotidiens
 * (`views_paid`, un par vidéo et par jour) sont **agrégés en une ligne** : sinon dormir trois
 * jours noie le journal.
 *
 * Aucune règle de jeu ici — que de la mise en mots.
 *
 * ## ⟦C57 · lot 1 · A-174⟧ Écrire une entrée AVEC DÉTAIL — le mode d'emploi des autres lots
 *
 * La règle « moins de texte » veut qu'une entrée du Journal reste **une ligne courte** (icône,
 * jour, UNE phrase de 12 mots au plus) et que ce que la ligne ne dit plus rouvre par le bouton
 * « Les dessous » de l'entrée, plus tard, quand le joueur veut relire. Le Journal devient
 * l'archive de ce que les écrans n'affichent plus.
 *
 * **Où l'entrée naît, et pourquoi le détail n'a pas besoin du serveur.** Une entrée est
 * fabriquée CÔTÉ CLIENT, par ce fichier, à chaque `dispatch` : `entreesDeResolution` (les
 * sorties du moteur : `events` + `log`) et `entreeInterface` (les lignes que l'interface écrit
 * elle-même). `localGameClient` ET `serverGameClient` appellent les deux : le serveur renvoie
 * les `events` (SPEC-API §5.5), il n'a aucune colonne de journal et n'en a pas besoin. Le journal
 * est écrit en JSON dans `localStorage` avec la run (`maxime.bac-a-sable.run.v1` en bac à sable,
 * `maxime.compte.meta.v1` par run en mode compte). Donc : **un `detail` qui est du JSON pur
 * (`DetailJournal`) survit au rechargement sans rien changer d'autre**, des deux côtés. Le
 * persister en base serait un chantier de schéma et de migration pour une mémoire d'interface
 * que le serveur ne lit jamais.
 *
 * **Comment un lot s'en sert** (le modèle à copier : la dérive de l'axe Produit, plus bas) :
 *
 * 1. Un fait du MOTEUR (issue d'événement, palier, crise, fin) : dans `ligneAvertissement` ou
 *    `ligneDeFait`, la ligne rendue porte `texte` COURT et
 *    `detail: detailJournal("Sujet", [{ paragraphes: [...] }, { titre: "Effets", lignes: [...] }])`.
 *    Le long texte reste dans `content/` : on le lit là, on ne le réécrit pas. Les tests
 *    (`journal.test.ts`, `FAITS_A_DETAIL`) vérifient que chaque fait à détail a une ligne courte
 *    et un détail qui survit à `JSON.stringify`. **Chaque lot y ajoute son fait.**
 * 2. Une ligne que l'INTERFACE écrit (dans un client, jamais dans un composant) :
 *    `entreeInterface(state, id, { icone, ton, texte, detail })`. Un composant n'écrit pas dans le
 *    journal : toute écriture passe par `dispatch`, c'est ce qui la fait persister.
 * 3. Afficher le détail ailleurs que dans le Journal (une fenêtre qui raconte le même fait) :
 *    `<DessousDuJournal detail={entree.detail} />` (`components/DessousDuJournal.tsx`).
 *
 * Deux limites connues. (a) Les autres consommateurs de `entreesDeResolution` (toasts de
 * `runStore`, les 10 dernières lignes de `Studio.tsx`, les faits du récap de `Equipe.tsx`)
 * montrent la ligne courte seule : pour y rouvrir le détail, ils posent `DessousDuJournal`.
 * (b) Le journal garde les `JOURNAL_MAX` dernières lignes ordinaires ET, à part, les `JOURNAL_DETAIL_MAX`
 * dernières lignes à détail (`limiterJournal`) : « Les dessous » ne s'efface plus avec le défilement des
 * lignes ordinaires, mais il s'efface quand même, du plus ancien au plus récent, au-delà du plafond.
 */

import {
  familleDuLog,
  type ActionType,
  type EngineEvent,
  type FamilleLog,
  type GameState,
  type LogEntry,
  type Recapitulatif,
} from "@maxime/shared";

import { contenu } from "./content";
import {
  adapterLigneDeTrame,
  ligneChoix,
  ligneDelegation,
  ligneFenetre,
  ligneNotification,
  titreCourt,
  type FaitsEvenement,
  type LigneEvenement,
} from "./evenementCourt";
import type { Etage } from "./escalier";
import { RECITS_DE_VICTOIRE, blocsDeCrise, entreeDeFin, varianteDeCrise } from "./fins";
import type { BlocDetail, DetailJournal, JournalEntry, LigneDetail, TonJournal } from "./gameClient";
import { jourSimule, nombre, points } from "./format";
import { systemeOuvert } from "./menage";
import { CHIFFRES_PALIERS } from "./paliers";
import { blocsDuRecap, ligneDuRecap } from "./recapDessous";

/** Lignes ordinaires (sans détail) conservées dans la run (le tableau de bord n'en montre que 10). */
export const JOURNAL_MAX = 80;

/** Lignes à détail (« Les dessous ») conservées EN PLUS des lignes ordinaires, les plus récentes d'abord. */
export const JOURNAL_DETAIL_MAX = 60;

/**
 * ⟦C57c · A-174⟧ **La rétention du Journal**, appelée par les trois endroits qui écrivent dans la run
 * (`localGameClient`, `serverGameClient`, `dev`). Deux plafonds indépendants :
 *
 * - les `JOURNAL_MAX` (80) dernières lignes ORDINAIRES (sans `detail`) ;
 * - PLUS les `JOURNAL_DETAIL_MAX` (60) dernières lignes À DÉTAIL.
 *
 * Avant, un seul plafond de 80 lignes coupait tout : à raison d'une ligne à détail par fait, les « dessous »
 * disparaissaient en quelques jours de jeu avec la masse des lignes ordinaires. Les deux familles ne se
 * font plus concurrence ; le Journal tient au plus 140 lignes.
 *
 * L'ordre d'origine est conservé (ce n'est pas un tri : on écarte les plus anciennes de chaque famille, les
 * survivantes restent à leur place relative). Une ligne a un détail quand `detail` est présent : la clé
 * absente (entrée d'avant le chantier, ou ligne ordinaire) et la clé `undefined` se valent, ce qui rend le
 * résultat identique avant et après un aller-retour `JSON.stringify` / `JSON.parse`.
 */
export function limiterJournal<T extends { readonly detail?: unknown }>(journal: readonly T[]): T[] {
  const gardee: boolean[] = new Array<boolean>(journal.length).fill(false);
  let ordinaires = 0;
  let aDetail = 0;
  for (let i = journal.length - 1; i >= 0; i--) {
    const detail = journal[i]!.detail;
    if (detail !== undefined && detail !== null) {
      aDetail += 1;
      gardee[i] = aDetail <= JOURNAL_DETAIL_MAX;
    } else {
      ordinaires += 1;
      gardee[i] = ordinaires <= JOURNAL_MAX;
    }
  }
  return journal.filter((_, i) => gardee[i]);
}

interface Ligne {
  texte: string;
  ton: TonJournal;
  icone: string;
  /** ⟦A-81⟧ La famille, choisie ICI fait par fait — une donnée, jamais un texte à relire. */
  famille: FamilleLog;
  /** ⟦C57 · lot 1⟧ Ce que la ligne ne dit plus — voir `detailJournal`. Absent = pas de bouton. */
  detail?: DetailJournal;
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦C57 · lot 1 · A-174⟧ Le détail d'une entrée
// ─────────────────────────────────────────────────────────────────────────────

/** Ce qu'on passe à `detailJournal` : un bloc, ou rien (un `cond && bloc` se lit sans `if`). */
export type BlocSource = {
  titre?: string;
  paragraphes?: readonly (string | null | undefined | false)[];
  lignes?: readonly (LigneDetail | null | undefined | false)[];
};

/**
 * **LA fonction que les lots appellent** pour donner un détail à une entrée. Rend un
 * `DetailJournal` propre, ou `undefined` quand il ne reste rien à ouvrir — l'entrée n'a alors
 * pas de bouton (jamais un « Les dessous » qui s'ouvre sur du vide).
 *
 * Elle normalise (espaces rognés, paragraphes et lignes vides écartés, blocs vides écartés) et
 * ne produit que des chaînes et des tableaux, sans clé `undefined` : le résultat passe
 * `JSON.stringify` puis `JSON.parse` à l'identique, c'est ce qui le fait survivre au F5.
 * **Elle ne tronque jamais** : un détail est de l'information qui change de place, pas qui
 * disparaît. Un détail démesuré est attrapé par `journal.test.ts`, pas rogné en silence.
 */
export function detailJournal(
  titre: string,
  blocs: readonly (BlocSource | null | undefined | false)[],
): DetailJournal | undefined {
  const propres: BlocDetail[] = [];
  for (const source of blocs) {
    if (!source) continue;
    const paragraphes = (source.paragraphes ?? []).flatMap((p) => (p && p.trim() ? [p.trim()] : []));
    const lignes = (source.lignes ?? []).flatMap((l): LigneDetail[] => {
      if (!l || !l.texte.trim()) return [];
      return [l.icone ? { icone: l.icone, texte: l.texte.trim() } : { texte: l.texte.trim() }];
    });
    if (paragraphes.length === 0 && lignes.length === 0) continue;
    const bloc: BlocDetail = {};
    if (source.titre?.trim()) bloc.titre = source.titre.trim();
    if (paragraphes.length > 0) bloc.paragraphes = paragraphes;
    if (lignes.length > 0) bloc.lignes = lignes;
    propres.push(bloc);
  }
  return propres.length > 0 ? { titre: titre.trim(), blocs: propres } : undefined;
}

/**
 * Tout le texte d'un détail, d'un trait : de quoi le chercher (le Journal cherche aussi dans ce
 * qu'il ne montre plus — « ce qui disparaît d'un écran force à chercher où c'est passé ») et de
 * quoi le mesurer dans les tests.
 */
export function texteDuDetail(detail: DetailJournal): string {
  const morceaux: string[] = [detail.titre];
  for (const bloc of detail.blocs) {
    if (bloc.titre) morceaux.push(bloc.titre);
    morceaux.push(...(bloc.paragraphes ?? []));
    morceaux.push(...(bloc.lignes ?? []).map((l) => l.texte));
  }
  return morceaux.join(" ");
}

/**
 * ⟦C57 · lot 2 · A-174⟧ Un fait d'événement (`game/evenementCourt.ts`) devient une ligne du Journal :
 * la ligne courte, et la situation, le choix et la morale en entier derrière « Les dessous ».
 */
function ligneDEvenement(l: LigneEvenement | null): Ligne | null {
  if (!l) return null;
  return {
    icone: l.icone,
    ton: l.ton,
    famille: "evenement",
    texte: l.texte,
    detail: detailJournal(l.sujet, l.blocs),
  };
}

/**
 * ⟦C57 · lot 3 · A-174⟧ **Une ligne COURTE, et le texte d'avant intact qui rouvre par « Les dessous ».**
 * La ligne est aussi un toast (`runStore`) et l'une des dix lignes de la fenêtre « Journal » de la
 * chambre : une phrase, 12 mots au plus. Ce que l'ancienne phrase disait de plus (le pourquoi, la
 * suite, le conseil) n'est pas réécrit : il est repris mot pour mot dans le détail.
 */
function courtAvecDessous(
  ligne: Omit<Ligne, "detail">,
  sujet: string,
  longs: readonly (string | null | undefined | false)[],
): Ligne {
  const detail = detailJournal(sujet, [{ paragraphes: longs }]);
  return detail ? { ...ligne, detail } : ligne;
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦SPEC-UI §3.6 · A-70 §5 · A-81⟧ Les familles — les vrais filtres du croquis
// ─────────────────────────────────────────────────────────────────────────────

export type FamilleFiltre = "tout" | FamilleLog;

/**
 * Les puces du Journal, dans l'ordre du croquis (`Tous | Buzz | ₵ | Technique`), complété de ce
 * que le journal contient réellement. **La source est le moteur** : `LogEntry.famille` est posée
 * par chaque émetteur, et les faits traduits ci-dessous choisissent la leur. Plus aucune icône
 * n'est interprétée.
 */
export const FAMILLES: readonly { id: FamilleFiltre; libelle: string; icone: string }[] = [
  { id: "tout", libelle: "Tout", icone: "" },
  { id: "buzz", libelle: "Buzz", icone: "💥" },
  { id: "argent", libelle: "₵ Argent", icone: "₵" },
  { id: "chaine", libelle: "La chaîne", icone: "📺" },
  { id: "technique", libelle: "Technique", icone: "🔧" },
  { id: "evenement", libelle: "Événements", icone: "📨" },
  { id: "quotidien", libelle: "Le quotidien", icone: "📻" },
];

/** L'icône d'une ligne de TRAME (le `log` du moteur) : celle de sa famille. */
const ICONE_TRAME: Record<FamilleLog, string> = {
  buzz: "💥",
  argent: "₵",
  chaine: "📺",
  technique: "🔧",
  evenement: "📨",
  quotidien: "📻",
};

/**
 * ⚠️ **Héritage, et rien d'autre.** Les entrées persistées en mémoire d'interface AVANT A-81
 * (localStorage) n'ont pas de famille : pour elles — et seulement elles — on retombe sur la
 * table par icône qu'⟦A-70⟧ avait livrée. Une entrée neuve porte toujours sa famille.
 */
const FAMILLE_PAR_ICONE_HERITEE = new Map<string, FamilleLog>([
  ...["📉", "⛔", "🙈", "💔", "🔥", "👤", "🫠", "🌫️", "⏹"].map((i) => [i, "buzz"] as const),
  ...["₵", "🏦", "🎟️"].map((i) => [i, "argent"] as const),
  ...["🏆", "🥇", "🥈", "⭐", "🚀", "📈", "🌲", "🧠", "🏁"].map((i) => [i, "chaine"] as const),
]);

export function familleDeEntree(entree: Pick<JournalEntry, "famille" | "icone">): FamilleLog {
  return entree.famille ?? FAMILLE_PAR_ICONE_HERITEE.get(entree.icone) ?? "quotidien";
}

function n(data: EngineEvent["data"], cle: string): number {
  const v = data[cle];
  return typeof v === "number" ? v : 0;
}

function s(data: EngineEvent["data"], cle: string): string | null {
  const v = data[cle];
  return typeof v === "string" ? v : null;
}

function b(data: EngineEvent["data"], cle: string): boolean {
  return data[cle] === true;
}

/** Un `warning` du moteur porte sa `cause` (ou son `scripted`) — chacune a sa phrase. */
function ligneAvertissement(data: EngineEvent["data"]): Ligne | null {
  const scripted = s(data, "scripted");
  if (scripted === "grand_saut") {
    // ⟦C57 · lot 2 · A-174⟧ L'issue du « Grand Saut » (l'événement `demission_job`) : la ligne tient en 10 mots
    // (« Le Grand Saut : +600 ₵ d'avance, 5 ₵ de charges par jour. »), la phrase entière est derrière « Les dessous ».
    // ⟦C60 · lot b⟧ Ni capital ni charges (le grand ménage les coupe) : le Grand Saut ne verse plus rien
    // et ne prélève plus rien, la ligne dit alors ce qui change vraiment, les vues se paient en Clicks.
    if (n(data, "capital") === 0 && n(data, "charges_par_jour") === 0) {
      return {
        icone: "₵",
        ton: "palier",
        famille: "argent",
        texte: "Le Grand Saut : tes vues rapportent des Clicks.",
        detail: detailJournal("Le Grand Saut", [
          {
            paragraphes: [
              "Le Grand Saut. À partir d'aujourd'hui, tes vues rapportent des Clicks : ils paient le Marché et ton Équipe.",
            ],
          },
        ]),
      };
    }
    const capital = nombre(n(data, "capital"));
    const charges = nombre(n(data, "charges_par_jour"));
    return {
      icone: "₵",
      ton: "palier",
      famille: "argent",
      texte: `Le Grand Saut : +${capital} ₵ d'avance, ${charges} ₵ de charges par jour.`,
      detail: detailJournal("Le Grand Saut", [
        { paragraphes: [`+${capital} ₵ d'avance. À partir d'aujourd'hui, ${charges} ₵ de charges tombent chaque jour.`] },
      ]),
    };
  }
  // ⟦A-15 §3⟧ **Le rival a pris de l'avance pendant que tu travaillais.** Le moteur ne l'émet
  // qu'au-dessus de son seuil (`SEUIL_ALERTE_RIVAL`) : « un PNJ qui prend 2 abonnés n'est que du
  // décor ». Aucune conséquence mécanique — c'est un miroir, pas une punition, d'où le ton
  // neutre et le refus d'écrire « attention ».
  if (scripted === "rival_progresse") {
    const ecart = n(data, "ecart");
    // ⟦C57 · lot 3⟧ La ligne dit ce qu'il a pris ; l'écart, qui faisait la fin de la phrase, passe
    // dans « Les dessous ».
    const nomRival = s(data, "nom") ?? "Un autre créateur";
    const gagnes = nombre(n(data, "gagnes"));
    const ecartDit = ecart > 0 ? `il te devance de ${nombre(ecart)}` : `tu le devances encore de ${nombre(-ecart)}`;
    return {
      icone: "👤",
      ton: "mal",
      famille: "buzz",
      texte: `${nomRival} a pris ${gagnes} abonnés pendant ce temps-là.`,
      detail: detailJournal(nomRival, [
        { paragraphes: [`${nomRival} a pris ${gagnes} abonnés pendant ce temps-là, ${ecartDit}.`] },
      ]),
    };
  }
  // ⟦SPEC-AXE-PRODUIT §5 / A-62⟧ **La dérive de l'axe Produit ↔ Authentique.**
  //
  // Le moteur pousse `{ kind: "warning", data: { derive, valeur, delta } }` et n'écrit aucun
  // texte : les mots vivent dans `content/axe-produit.json`. Sans cette branche, l'axe
  // basculerait sans que le joueur en entende jamais parler — exactement la rupture n° 2 de
  // `docs/AUDIT-MAILLONS.md`, où `extinction_imminente` était émis par le moteur et traduit
  // par personne, donc invisible à l'écran comme dans les toasts.
  //
  // ⚠️ **Aucun vainqueur moral** (§6.4) : le ton est `info`, jamais `mal`. Le jeu montre le
  // prix des deux routes, il n'en récompense — et il n'en punit — aucune.
  const derive = s(data, "derive");
  if (derive !== null) {
    const dit = contenu().axeProduit.derives.find((d) => d.id === derive);
    if (dit) {
      // ⟦C57 · lot 1 · A-174⟧ **Le premier fait à détail, et le modèle à copier.** Avant : une
      // ligne de 33 mots (`titre — ligne morale`). Maintenant : le TITRE de la dérive (« Il a
      // changé », une réplique du public) fait la ligne, et ce que le public en pense (`ligne`,
      // `morale`, intacts dans `content/axe-produit.json`) rouvre par « Les dessous ». Rien n'est
      // réécrit : le texte long est lu tel quel.
      return {
        icone: dit.icone,
        ton: "info",
        famille: "chaine",
        texte: dit.titre,
        detail: detailJournal(dit.titre, [{ paragraphes: [dit.ligne, dit.morale] }]),
      };
    }
  }
  // ⟦SPEC-PRODUCTEUR §5.2 / §1.5 · A-81⟧ **L'arrêt de la délégation** — `warning { arret }`,
  // émis par `laisser_tourner` « pour que le journal et les toasts en héritent ». Il tombait
  // dans le `default: return null` ci-dessous : émis, jamais traduit — le trou exact
  // d'`extinction_imminente`. Le libellé vient du contenu (`producteur.json`), jamais le nom
  // du motif moteur.
  const arret = s(data, "arret");
  if (arret !== null) {
    const libelle = contenu().equipe.libelles.arret[arret] ?? "On a besoin de toi.";
    // ⟦C57 · lot 3⟧ Le fait à l'écran, le motif (qui vient du contenu) dans « Les dessous ».
    return courtAvecDessous(
      { icone: "🎬", ton: "info", famille: "technique", texte: "L'équipe te rend la main." },
      "L'équipe te rend la main",
      [`L'équipe te rend la main. ${libelle}`],
    );
  }
  switch (s(data, "cause")) {
    case "dernier_avertissement_burnout":
      return {
        icone: "🫠",
        ton: "mal",
        famille: "buzz",
        // ⟦C57 · lot 3⟧ Une phrase à l'écran (c'est aussi un toast) ; la phrase d'avant rouvre par « Les dessous ».
        texte: "Dernier avertissement : encore une crise et tu t'arrêtes.",
        detail: detailJournal("Dernier avertissement", [
          {
            paragraphes: [
              "Dernier avertissement : encore une crise et tu t'arrêtes pour de bon. Dors, ou prends une vraie pause.",
            ],
          },
        ]),
      };
    case "arc_chute_ouvert":
      return {
        icone: "📉",
        ton: "mal",
        famille: "buzz",
        // ⟦C57 · lot 3⟧ Une seule phrase ; le bandeau « Le public sent le sang » de la chambre porte les deux seuils.
        // ⟦C60 bis · A-177⟧ Éteinte (arbitrage n° 2, 03/10/2026), la jauge ne se nomme ni ne se chiffre plus.
        texte: systemeOuvert("reputation")
          ? `Réputation en chute (${Math.round(n(data, "reputation"))}) : le public sent le sang.`
          : "Le public sent le sang.",
      };
    case "strike":
      return {
        icone: "⛔",
        ton: "mal",
        famille: "buzz",
        // ⟦C57 · lot 3⟧ Le compte, c'est ce qui compte ; « à 3, la chaîne saute » vit dans le bandeau de la chambre.
        texte: `Strike : ${nombre(n(data, "actifs"))} actif(s) sur ${nombre(n(data, "fatal_a"))}.`,
      };
    // ⟦A-50 §3 levier 1 · A-108⟧ « Je gère » : le joueur a reporté un `pending_event` plutôt
    // que de le trancher — le canal existe dès l'émission, pas de strike-mort-en-production bis.
    case "evenement_reporte": {
      // ⟦C57 · lot 2⟧ La ligne dit QUI a tranché ; l'explication (l'issue par défaut) rejoint « Les dessous ».
      const id = s(data, "event_id") ?? "";
      const ligne = ligneDEvenement(ligneDelegation(id, s(data, "choice_id"), { type: "report" }));
      if (ligne) return ligne;
      // ⟦C57 · lot 3⟧ Repli (événement inconnu du contenu) : le nom fait la ligne, « à l'issue par défaut » est derrière « Les dessous ».
      const evt = contenu().parEvenement.get(id);
      const nom = evt ? titreCourt(evt) : undefined;
      return courtAvecDessous(
        { icone: "⏳", ton: "info", famille: "evenement", texte: `Tu gères : « ${nom ?? id} » se règle sans toi.` },
        nom ?? "Tu gères",
        [`Tu gères : « ${nom ?? id} » se règle sans toi, à l'issue par défaut.`],
      );
    }
    // ⟦C8 · A-120⟧ « Le manager tranche » : le minuteur client a expiré sur un `pending_event`
    // (ou le joueur a délégué la décision). Distinct d'`evenement_reporte` : ici c'est
    // l'EMPLOYÉ qui a choisi, jamais le joueur.
    case "manager_tranche": {
      const id = s(data, "event_id") ?? "";
      const managerId = s(data, "manager_id");
      const nomManager = (managerId ? contenu().parManager.get(managerId)?.nom : undefined) ?? "Ton manager";
      // ⟦C57 · lot 2⟧ Idem : la ligne dit qui a tranché quoi, « pendant que tu étais ailleurs » rejoint « Les dessous ».
      const ligne = ligneDEvenement(ligneDelegation(id, s(data, "choice_id"), { type: "manager", nomManager }));
      if (ligne) return ligne;
      // ⟦C57 · lot 3⟧ Repli (événement inconnu du contenu) : qui tranche quoi à l'écran, « pendant que tu étais ailleurs » dans « Les dessous ».
      const evt = contenu().parEvenement.get(id);
      const nom = evt ? titreCourt(evt) : undefined;
      return courtAvecDessous(
        {
          icone: "⏳",
          ton: "info",
          famille: "evenement",
          texte: `${nomManager} tranche « ${nom ?? id} » à ta place.`,
        },
        nom ?? "Le manager tranche",
        [`${nomManager} tranche « ${nom ?? id} » à ta place, pendant que tu étais ailleurs.`],
      );
    }
    case "faillite_imminente":
      return {
        icone: "🏦",
        ton: "mal",
        famille: "argent",
        // ⟦C57 · lot 3⟧ Une phrase : le solde et l'échéance, les deux seuls chiffres qui comptent.
        texte: `Compte dans le rouge (${nombre(Math.round(n(data, "money")))} ₵) : ${nombre(n(data, "jours_restants"))} jours avant la faillite.`,
      };
    // ⟦SPEC-PRODUCTEUR §6.2 / §11.6 · M3.29⟧ **Le salaire impayé se dit PARTOUT, plus
    // seulement sur l'écran Équipe.** C'était exactement le défaut d'`extinction_imminente`
    // avant ⟦A-58⟧ (voir plus bas) : émis par le moteur, traduit par personne — un joueur qui
    // n'ouvrait pas l'écran Équipe ne voyait rien avant que le producteur parte. Cette ligne
    // nourrit le journal ET les toasts (runStore les fabrique depuis ces entrées).
    case "equipe_impayee": {
      const etage = s(data, "etage") === "manager" ? "Ton manager" : "Ta productrice";
      const jours = n(data, "jours_impayes");
      if (b(data, "parti")) {
        // ⟦C57 · lot 3⟧ « Elle s'en va » à l'écran ; le motif et la marche perdue dans « Les dessous ».
        const accord = etage === "Ta productrice" ? "e" : "";
        return {
          icone: "🎬",
          ton: "mal",
          famille: "argent",
          texte: `${etage} n'a pas été payé${accord} : ${etage === "Ta productrice" ? "elle" : "il"} s'en va.`,
          detail: detailJournal(etage === "Ta productrice" ? "Ta productrice s'en va" : "Ton manager s'en va", [
            {
              paragraphes: [
                `${etage} n'a pas été payé${accord} trois jours de suite : ${etage === "Ta productrice" ? "elle" : "il"} s'en va. Tu redescends d'une marche de l'escalier.`,
              ],
            },
          ]),
        };
      }
      // ⟦C57 · lot 3⟧ L'attente à l'écran ; la règle des trois jours d'affilée dans « Les dessous ».
      return courtAvecDessous(
        {
          icone: "🎬",
          ton: "mal",
          famille: "argent",
          texte: `Salaire impayé : ${etage.toLowerCase()} attend depuis ${nombre(jours)} jour${jours > 1 ? "s" : ""}.`,
        },
        "Salaire impayé",
        [
          `Salaire impayé — ${etage.toLowerCase()} attend depuis ${nombre(jours)} jour${jours > 1 ? "s" : ""}. Au troisième d'affilée, c'est le départ.`,
        ],
      );
    }
    // ⟦PLAN §4.10 · A-74⟧ La zone rouge a aussi sa touche humaine : le moteur émet le SMS une
    // fois par épisode (`smsDeTaMere`, hystérésis au retour en vert), et sans cette branche il
    // serait tombé dans le `default: return null` — exactement le trou qui a rendu
    // `extinction_imminente` invisible pendant une journée (voir le commentaire plus bas).
    case "sms_de_ta_mere":
      return {
        icone: "📱",
        ton: "info",
        famille: "quotidien",
        // ⟦C57 · lot 3⟧ La réplique qui pique reste à l'écran ; le SMS entier et le « oui oui » du pouce
        // gauche rouvrent par « Les dessous ». Rien n'est réécrit.
        texte: "SMS de ta mère : « Tu manges, le dimanche ? »",
        detail: detailJournal("SMS de ta mère", [
          {
            paragraphes: [
              "SMS de ta mère : « J'ai vu ta dernière vidéo, tu as une petite mine. Tu manges, le dimanche ? » Tu réponds « oui oui » du pouce gauche, sans lâcher la timeline.",
            ],
          },
        ]),
      };
    // ⟦PLAN §4.11⟧ Les excuses ratées ont **deux causes**, et elles n'appellent pas le même
    // geste : sans « Gestion de crise » il faut aller acheter le nœud, à chaud il fallait
    // simplement attendre. « Les excuses sont tombées à plat » était vrai dans les deux cas et
    // n'apprenait rien ni dans l'un ni dans l'autre — le moteur envoyait pourtant déjà de quoi
    // trancher (`a_le_skill`, `a_laisse_retomber`).
    case "excuses_ratees": {
      const arc = b(data, "arc_actif");
      const pourquoi = !b(data, "a_le_skill")
        ? "tu ne sais pas encore désamorcer une crise"
        : "tu as filmé à chaud, le buzz n'était pas retombé";
      // ⟦C57 · lot 3⟧ Le fait à l'écran, le pourquoi (qui dépend du nœud acheté) dans « Les dessous ».
      return {
        icone: "🙈",
        ton: "mal",
        famille: "buzz",
        texte: "Les excuses sont tombées à plat.",
        detail: detailJournal("Excuses ratées", [
          {
            paragraphes: [
              `Les excuses sont tombées à plat : ${pourquoi}.${arc ? "" : " Ça laisse des traces, mais tu es encore là."}`,
            ],
          },
        ]),
      };
    }
    // ⟦A-58 · §9.4bis⟧ **Le télégraphiage du Fantôme.** ⟦PLAN §4.11⟧ exige que tout game over
    // s'annonce, et la cinquième fin est celle qui en a le plus besoin : les quatre autres
    // arrivent avec fracas — on brûle, on se fait bannir, on fait faillite — alors que
    // l'extinction est **silencieuse par nature**. Sans cette ligne, elle tombait sans un mot.
    //
    // ⚠️ L'avertissement était **émis correctement par le moteur depuis A-58** et tombait dans
    // le `default: return null` ci-dessous. Le maillon manquant n'était ni le calcul ni
    // l'événement : c'était **la traduction**. Un `default` qui rend `null` ne se plaint jamais
    // de ce qu'il avale — c'est ce qui a rendu le trou invisible pendant une journée entière.
    //
    // Les deux voies disent la même échéance et **pas la même chose** : l'une constate que
    // plus personne ne regarde, l'autre que plus personne ne poste. Écrire un texte unique
    // aurait été plus court et aurait donné au joueur un conseil faux une fois sur deux.
    case "extinction_imminente": {
      const restants = n(data, "jours_restants");
      const echeance =
        restants > 0 ? `Encore ${nombre(restants)} jours comme ça et c'est fini.` : "C'est maintenant.";
      // ⟦A-79⟧ La voie précoce est « un avertissement, puis le couperet » : le texte est net,
      // et il distingue « jamais publié » (la leçon de début de partie, dans les mots de
      // Mathieu) du silence d'un créateur établi.
      //
      // ⟦C57 · lot 3⟧ La ligne dit le fait et l'échéance en UNE phrase ; la phrase d'avant, mot pour
      // mot, rouvre par « Les dessous ».
      const fin = restants > 0 ? `fin dans ${nombre(restants)} jours` : "c'est maintenant";
      if (s(data, "voie") === "jamais_decolle") {
        const jamaisPublie = b(data, "jamais_publie");
        const ecoules = nombre(n(data, "jours_ecoules"));
        return courtAvecDessous(
          {
            icone: "🌫️",
            ton: "mal",
            famille: "buzz",
            texte: jamaisPublie ? `Rien publié : ${fin}.` : `${ecoules} jours sans rien publier : ${fin}.`,
          },
          "La chaîne s'efface",
          [
            jamaisPublie
              ? `Tu n'as encore rien publié — MeTube oublie vite. ${echeance}`
              : `${ecoules} jours sans rien publier. La chaîne s'efface. ${echeance}`,
          ],
        );
      }
      return courtAvecDessous(
        { icone: "🌫️", ton: "mal", famille: "buzz", texte: `Plus personne ne regarde : ${fin}.` },
        "Plus personne ne regarde",
        [
          `Plus personne ne regarde : tes dernières vidéos ne prennent pas et la chaîne ne rapporte plus rien depuis ${nombre(n(data, "jours_ecoules"))} jours. ${echeance}`,
        ],
      );
    }
    default:
      return null;
  }
}

function ligneDeFait(event: EngineEvent, tour: readonly EngineEvent[]): Ligne | null {
  const d = event.data;
  switch (event.kind) {
    case "milestone": {
      const plaque = s(d, "plaque");
      const palier = n(d, "palier");
      // ⟦C57 · lot 3⟧ La pop-up du palier ne garde que le chiffre en gros et UNE phrase : les « vrais
      // chiffres du vrai YouTube » (`game/paliers.ts`, intacts) rejoignent la ligne du Journal, qui
      // les rouvre par « Les dessous ». Un palier que la table ne connaît pas n'a pas de détail.
      const chiffres = CHIFFRES_PALIERS[palier];
      return {
        icone: plaque === "bouton_or" ? "🥇" : plaque === "bouton_argent" ? "🥈" : "🏆",
        ton: "palier",
        famille: "chaine",
        texte: `Palier ${nombre(palier)} abonnés franchi : +${points(n(d, "pc_gagnes"))}.`,
        ...(chiffres ? { detail: detailJournal(chiffres.titre, [{ paragraphes: [chiffres.texte] }]) } : {}),
      };
    }
    case "level_up":
      return {
        icone: "⭐",
        ton: "bien",
        famille: "chaine",
        texte: `Niveau de chaîne ${n(d, "level")} : +${points(n(d, "pc_gagnes"))}.`,
      };
    case "viral":
      return {
        icone: "🚀",
        ton: "bien",
        famille: "chaine",
        texte: `Ça part en orbite : ${nombre(n(d, "vues_pic"))} vues sur le pic.`,
      };
    case "subs_lost":
      return n(d, "subs_lost") > 0
        ? {
            icone: "💔",
            ton: "mal",
            famille: "buzz",
            texte: `${nombre(n(d, "subs_lost"))} ${n(d, "subs_lost") > 1 ? "désabonnements" : "désabonnement"} pendant la nuit.`,
          }
        : null;
    case "rent_paid":
      return n(d, "vues") > 0
        ? {
            icone: "🌲",
            ton: "bien",
            famille: "chaine",
            texte: `Rente des vidéos intemporelles : +${nombre(n(d, "vues"))} vues, +${nombre(n(d, "abonnes"))} abonnés pendant que tu dormais.`,
          }
        : null;
    case "crisis_started": {
      // ⟦C57 · lot 7 · A-174⟧ La ligne garde les chiffres ; le récit de la crise, l'assurance burn-out
      // et ce qui attend après s'ouvrent par « Les dessous », comme sur l'écran de crise (`Crise.tsx`,
      // même assemblage : `blocsDeCrise`). La variante se lit sur les AUTRES faits du même tour : un
      // game over burn-out = la crise terminale, le dernier avertissement = la première crise.
      const variante = varianteDeCrise({
        terminal: tour.some((e) => e.kind === "game_over" && s(e.data, "cause") === "burnout_terminal"),
        dernier_avertissement: tour.some(
          (e) => e.kind === "warning" && s(e.data, "cause") === "dernier_avertissement_burnout",
        ),
      });
      return {
        icone: "🧠",
        ton: "mal",
        famille: "buzz",
        texte: `Crise : ${nombre(n(d, "jours"))} jours d'arrêt forcé, ${nombre(n(d, "abonnes_perdus"))} abonnés perdus.`,
        detail: detailJournal(
          "La crise, en détail",
          blocsDeCrise({ jours: n(d, "jours"), rente_deduite: n(d, "rente_deduite"), variante }),
        ),
      };
    }
    case "crisis_ended":
      return { icone: "🧠", ton: "info", famille: "buzz", texte: "Tu remontes la pente. Doucement." };
    // ⟦A-15 §4⟧ **Une fenêtre vient de s'ouvrir.** C'est le seul événement du moteur qui ne
    // BLOQUE pas : sans cette ligne, le joueur n'apprenait l'existence de l'offre qu'au
    // récapitulatif qui lui annonce sa fermeture. Les tuiles bloquantes (`pending`), elles, ont
    // déjà leur modale — les annoncer ici les dirait deux fois.
    case "event_drawn": {
      const id = s(d, "event_id") ?? "";
      // ⟦C57 · lot 2⟧ Une notification (`issue`, sans `choix`) n'a pas de fenêtre : sa ligne est la situation
      // courte, et la situation entière, ce que tu en as fait et la morale rejoignent « Les dessous ».
      if (!b(d, "opportunite")) return ligneDEvenement(ligneNotification(id));
      // Une offre à fenêtre : « Une fenêtre s'ouvre : « Titre », jusqu'au jour 12. » et l'offre entière derrière.
      const ligne = ligneDEvenement(ligneFenetre(id, nombre(jourSimule(n(d, "expire_h")))));
      if (ligne) return { ...ligne, ton: "bien" };
      // ⟦C57 · lot 3⟧ Repli (événement inconnu du contenu) : la fenêtre et son échéance à l'écran, la suite dans « Les dessous ».
      const evt = contenu().parEvenement.get(id);
      const nom = evt ? titreCourt(evt) : undefined;
      const expire = n(d, "expire_h");
      return courtAvecDessous(
        {
          icone: "🎟️",
          ton: "bien",
          famille: "evenement",
          texte: `Une fenêtre s'ouvre${nom ? ` : « ${nom} »` : ""}, jusqu'au jour ${nombre(jourSimule(expire))}.`,
        },
        nom ?? "Une fenêtre s'ouvre",
        [
          `Une fenêtre s'ouvre${nom ? ` : « ${nom} »` : ""}. Tu as jusqu'au jour ${nombre(jourSimule(expire))} pour répondre — après, ils prennent quelqu'un d'autre.`,
        ],
      );
    }
    case "trend_changed": {
      const raison = s(d, "raison");
      return raison === "rotation" || raison === "morte"
        ? { icone: "🔥", ton: "info", famille: "chaine", texte: "Une tendance vient d'en remplacer une autre." }
        : null;
    }
    case "warning":
      return ligneAvertissement(d);
    // ⟦A-20 · A-74⟧ **La remontée se RACONTE** — « au moment où la remontée s'amorce ». Les
    // deux faits existaient depuis M1.16 (`gameover.ts` les émet) et tombaient dans le
    // `default: return null` : l'arc de rédemption commençait sans que le jeu le dise.
    case "event_resolved": {
      // ⟦C60 bis · A-177⟧ La réputation est retirée de l'écran : la remontée et les excuses se disent sans elle.
      const reputationVisible = systemeOuvert("reputation");
      switch (s(d, "cause")) {
        case "arc_chute_ferme":
          return courtAvecDessous(
            {
              icone: "🌤",
              ton: "bien",
              famille: "buzz",
              // ⟦C60 bis · A-177⟧ La réputation est retirée de l'écran : même fait, sans la jauge ni son chiffre.
              texte: reputationVisible
                ? `La remontée : réputation sortie de la zone rouge (${Math.round(n(d, "reputation"))}).`
                : "La remontée : le public recommence à t'écouter.",
            },
            "La remontée",
            [
              reputationVisible
                ? `La remontée. Ta réputation est sortie de la zone rouge (${Math.round(n(d, "reputation"))}) — le public recommence à écouter la version longue.`
                : "La remontée : le public recommence à écouter la version longue.",
            ],
          );
        case "excuses_reussies":
          return courtAvecDessous(
            {
              icone: "🕊",
              ton: "bien",
              famille: "buzz",
              texte: reputationVisible
                ? `Les excuses passent : +${nombre(n(d, "abonnes_rendus"))} abonnés, +${nombre(n(d, "reputation_rendue"))} de réputation.`
                : `Les excuses passent : +${nombre(n(d, "abonnes_rendus"))} abonnés.`,
            },
            "Les excuses passent",
            [
              reputationVisible
                ? `Les excuses passent. ${nombre(n(d, "abonnes_rendus"))} abonnés reviennent, et on te rend ${nombre(n(d, "reputation_rendue"))} de réputation. Tu avais laissé retomber : ça s'entend.`
                : `Les excuses passent. ${nombre(n(d, "abonnes_rendus"))} abonnés reviennent. Tu avais laissé retomber : ça s'entend.`,
            ],
          );
        default: {
          // ⟦C57 · lot 2⟧ Le joueur (ou « Je gère », ou le manager) a tranché un événement : « Titre : Choix »
          // à la ligne, la situation, le choix long et la morale longue derrière « Les dessous ».
          const eventId = s(d, "event_id");
          const choixId = s(d, "choice_id");
          return eventId && choixId ? ligneDEvenement(ligneChoix(eventId, choixId)) : null;
        }
      }
    }
    case "victory": {
      // ⟦C57 · lot 7 · A-174⟧ Une phrase sur la ligne ; la narration de la fin que le moteur a nommée
      // (La Rue, CEO, La Communauté), intacte, derrière « Les dessous ».
      const fin = RECITS_DE_VICTOIRE[(s(d, "ending") ?? "") as keyof typeof RECITS_DE_VICTOIRE];
      return {
        icone: "🏁",
        ton: "palier",
        famille: "chaine",
        texte: `LE MILLION : ${nombre(n(d, "subscribers"))} abonnés au jour ${nombre(n(d, "jour"))}, une rue à ton nom.`,
        detail: fin ? detailJournal(`Ta fin : ${fin.titre}`, [{ paragraphes: [fin.texte] }]) : undefined,
      };
    }
    case "game_over": {
      // ⟦C57 · lot 7 · A-174⟧ **L'épitaphe rejoint le Journal** : une ligne courte (`titre : épitaphe`),
      // la narration de la fin entière derrière « Les dessous ». Une cause inconnue garde la ligne
      // d'avant (la cause brute se voit, elle ne disparaît pas).
      const fin = entreeDeFin(s(d, "cause"), b(d, "legende"));
      if (!fin) return { icone: "⏹", ton: "mal", famille: "buzz", texte: `Fin de run : ${s(d, "cause") ?? "game over"}.` };
      return {
        icone: fin.icone,
        ton: "mal",
        famille: "buzz",
        texte: fin.texte,
        detail: detailJournal(fin.titreDetail, fin.blocs),
      };
    }
    default:
      return null;
  }
}

/** Les versements quotidiens, en une seule ligne (sinon trois nuits = quarante lignes). */
function ligneVersements(events: readonly EngineEvent[]): Ligne | null {
  let vues = 0;
  let abonnes = 0;
  let argent = 0;
  let compte = 0;
  for (const e of events) {
    if (e.kind !== "views_paid") continue;
    compte++;
    vues += n(e.data, "vues");
    abonnes += n(e.data, "abonnes");
    argent += n(e.data, "argent");
  }
  if (compte === 0 || (vues === 0 && abonnes === 0)) return null;
  const bouts = [`+${nombre(vues)} vues`];
  if (abonnes !== 0) bouts.push(`${abonnes > 0 ? "+" : ""}${nombre(abonnes)} abonnés`);
  if (argent > 0) bouts.push(`+${nombre(Math.round(argent))} ₵`);
  return { icone: "📈", ton: "bien", famille: "chaine", texte: bouts.join(" · ") };
}

/**
 * ⟦C60 · intégration · A-177⟧ **Le moteur écrit l'IDENTIFIANT du sujet dans ses lignes de trame** (« Publié :
 * « enferme_72h_cave » (trend…) »), il ne connaît que les identifiants. Le joueur lit donc des noms de base de
 * données dans son Journal : l'un d'eux (`boutique_qui_nexiste_pas`) contenait même un mot du ménage. À
 * l'affichage on remet le NOM du sujet à la place de son identifiant ; un identifiant inconnu reste tel quel.
 * Le texte du moteur n'est pas touché (les tests, la recherche et le serveur lisent le même).
 */
export function nomsDeSujetsDansLaTrame(texte: string): string {
  const parSujet = contenu().parSujet;
  const parSkill = contenu().parSkill;
  // ⟦C62 · lot arbre⟧ Même défaut pour l'achat d'un nœud de l'Arbre : « Nœud « le_manager » débloqué pour 16 PC »
  // laissait lire l'identifiant. Le joueur lit le NOM de la compétence (« « Le manager » pris pour 16 PC »).
  texte = texte.replace(/Nœud « ([a-z0-9_]+) » débloqué/g, (tel, id: string) => {
    const skill = parSkill.get(id) as { nom?: string } | undefined;
    return skill?.nom ? `« ${skill.nom} » pris` : tel;
  });
  return texte.replace(/« ([a-z0-9_]+) »/g, (tel, id: string) => {
    const sujet = parSujet.get(id);
    return sujet ? `« ${sujet.nom} »` : tel;
  });
}

/**
 * Traduit une résolution en lignes de journal. `prochainId` est le compteur de la run : les
 * ids sont stables et croissants, React s'en sert comme clé.
 */
export function entreesDeResolution(
  events: readonly EngineEvent[],
  log: readonly LogEntry[],
  state: GameState,
  prochainId: number,
  /**
   * ⟦C57 · lot 3⟧ Le récapitulatif des jours écoulés de cette action, s'il a traversé un jour : sa
   * pop-up ne garde que les soldes et une phrase, le calcul complet rejoint le Journal (`ligneDuRecap`,
   * `blocsDuRecap`). Facultatif : sans lui, le journal est celui d'avant le chantier, au caractère près.
   */
  recap?: { recap: Recapitulatif; action?: ActionType } | null,
): JournalEntry[] {
  const lignes: Ligne[] = [];

  // ⟦C57 · lot 2⟧ Ce que la résolution dit des événements : les lignes de trame que le moteur écrit lui-même
  // autour d'eux (« Choix : « … » », « Tu remets… », la notification en entier) sont remplacées par les lignes
  // courtes à détail de `ligneDeFait`, ou raccourcies (le titre court). Rien n'est perdu : le long est dans le détail.
  const faits: { -readonly [K in keyof FaitsEvenement]: FaitsEvenement[K][number][] } = { resolus: [], tires: [], delegues: [] };
  for (const e of events) {
    const eventId = s(e.data, "event_id");
    if (!eventId) continue;
    const choixId = s(e.data, "choice_id");
    if (e.kind === "event_resolved" && choixId) faits.resolus.push({ event_id: eventId, choice_id: choixId });
    if (e.kind === "event_drawn") faits.tires.push({ event_id: eventId, pending: b(e.data, "pending"), opportunite: b(e.data, "opportunite") });
    if (e.kind === "warning" && ["evenement_reporte", "manager_tranche"].includes(s(e.data, "cause") ?? "")) faits.delegues.push({ event_id: eventId });
  }

  // ⟦A-81⟧ La trame porte sa famille depuis le moteur ; l'icône en découle, elle ne la décide pas.
  for (const l of log) {
    const adapte = adapterLigneDeTrame(l.text, faits);
    if (adapte === null) continue;
    const famille = familleDuLog(l);
    lignes.push({ texte: nomsDeSujetsDansLaTrame(adapte ?? l.text), ton: "info", icone: ICONE_TRAME[famille], famille });
  }

  const versements = ligneVersements(events);
  if (versements) lignes.push(versements);

  for (const e of events) {
    const ligne = ligneDeFait(e, events);
    if (ligne) lignes.push(ligne);
  }

  // ⟦C57 · lot 3⟧ Le récap des jours : une ligne « 3 jours ont passé · +45 👥 · −20 ⚡ » (ton `info` : jamais un
  // toast, la pop-up vient de le dire) et, derrière « Les dessous », le calcul complet de la pop-up.
  if (recap && recap.recap.jours_traverses > 0) {
    const detail = detailJournal("Les jours écoulés", blocsDuRecap(recap.recap, recap.action));
    lignes.push({
      icone: "🕰",
      ton: "info",
      famille: "quotidien",
      texte: ligneDuRecap(recap.recap),
      ...(detail ? { detail } : {}),
    });
  }

  return lignes.map(({ detail, ...ligne }, i) => ({
    id: prochainId + i,
    turn: state.turn,
    jour: jourSimule(state.clock_h),
    ...ligne,
    // Jamais de clé `detail: undefined` : une entrée sans détail n'a pas la clé du tout.
    ...(detail ? { detail } : {}),
  }));
}

/**
 * ⟦C57 · lot 3⟧ La ligne que l'interface écrit quand un étage de l'escalier s'ouvre : l'annonce COURTE
 * (`annonce_courte`, une phrase) fait la ligne, l'annonce d'avant (`annonce`, intacte) rouvre par « Les
 * dessous ». À passer à `entreeInterface` : `localGameClient` et `serverGameClient`, côte à côte.
 */
export function ligneDeverrouillage(e: Etage): {
  icone: string;
  ton: "deverrouillage";
  texte: string;
  detail?: DetailJournal;
} {
  const detail = detailJournal(e.titre, [{ paragraphes: [e.annonce] }]);
  return { icone: e.icone, ton: "deverrouillage", texte: e.annonce_courte, ...(detail ? { detail } : {}) };
}

/**
 * Une ligne écrite par l'interface elle-même (déverrouillage d'un étage, création de run).
 * ⟦C57 · lot 1⟧ Elle peut porter un `detail` (voir `detailJournal`) : même mécanisme, même
 * persistance que pour un fait du moteur. À n'appeler que depuis un client (`dispatch`) : c'est
 * ce qui écrit l'entrée dans la run, donc dans `localStorage`.
 */
export function entreeInterface(
  state: GameState,
  id: number,
  ligne: { texte: string; ton: TonJournal; icone: string; famille?: FamilleLog; detail?: DetailJournal },
): JournalEntry {
  // ⟦A-81⟧ Un étage qui s'ouvre est de l'outillage (« ce que ta chambre a gagné ») ; le reste
  // de ce que l'interface écrit elle-même (naissance de la chaîne, télécommande) est la trame.
  const { detail, ...reste } = ligne;
  const famille = reste.famille ?? (reste.ton === "deverrouillage" ? "technique" : "quotidien");
  return { id, turn: state.turn, jour: jourSimule(state.clock_h), ...reste, famille, ...(detail ? { detail } : {}) };
}
